import { isEqual, isNil } from 'lodash';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useToast } from '@shared/components/Toast';
import {
  isComponentExcludedFromCollectionView,
  isComponentHiddenInView,
} from '@shared/data/cards/utils';
import {
  useSaveViewLayoutOrder,
  useViewTemplateLayout,
  useWorkflowTemplate,
} from '@shared/scenes/workflows/hooks';
import { getComponentsInLayoutOrder } from '@shared/scenes/workflows/resolveViewTemplateLayout';
import { isManagedComponent } from '@shared/scenes/workflows/utils';
import { API } from '@shared/types/api';
import { i18n } from '@shared/util/i18n';
import { JsonStorage } from '@shared/util/storage';
import type { UpdateComponentOrder, ViewLayoutActions, ViewLayoutState } from './ViewLayoutContext';

// Kept as long as the view template's own unsaved edits, which it is reverted and saved with.
const VIEW_LAYOUT_TTL = 1000 * 60 * 60 * 24; // 1 day

export function getViewLayoutStorageKey(viewTemplateId: string): string {
  return `viewLayout-${viewTemplateId}`;
}

export interface ViewLayoutDraft {
  state: ViewLayoutState;
  actions: ViewLayoutActions;
}

interface UseViewLayoutDraftProps {
  workflowTemplateId: string;
  viewTemplateId: string;
  /** The view template with its unsaved edits, which decides which fields are visible. */
  viewTemplate: API.ViewTemplateFragment;
  savedViewTemplate: API.ViewTemplateFragment;
  preserveEdits: boolean;
}

/**
 * A collection view's field order, as an unsaved draft alongside the view template's own.
 *
 * The draft is the list of visible fields in order, which is exactly what gets saved: the layout
 * holds only visible fields, because the view template alone decides visibility. A view with no
 * saved layout, or one that fails to load, shows its fields in workflow template order.
 */
export function useViewLayoutDraft({
  workflowTemplateId,
  viewTemplateId,
  viewTemplate,
  savedViewTemplate,
  preserveEdits,
}: UseViewLayoutDraftProps): ViewLayoutDraft {
  const { layout: savedLayout, loading } = useViewTemplateLayout(
    workflowTemplateId,
    viewTemplateId,
  );
  const { workflowTemplate } = useWorkflowTemplate(workflowTemplateId);
  const saveLayoutOrder = useSaveViewLayoutOrder();
  const toast = useToast();
  const [draftOrder, setDraftOrder] = useState<string[] | null>(null);

  const components = useMemo(() => workflowTemplate?.components ?? [], [workflowTemplate]);

  const layout = useMemo(
    () => (isNil(draftOrder) ? (savedLayout ?? null) : toDraftLayout(draftOrder, savedLayout)),
    [draftOrder, savedLayout],
  );

  const visibleOrder = useMemo(
    () => getVisibleFieldOrder(components, layout, viewTemplate),
    [components, layout, viewTemplate],
  );
  const savedOrder = useMemo(
    () => getVisibleFieldOrder(components, savedLayout, savedViewTemplate),
    [components, savedLayout, savedViewTemplate],
  );
  const isModified = !isNil(draftOrder) && !isEqual(visibleOrder, savedOrder);
  // Until both the fields and the saved layout are in, the two orders can't be compared.
  const isReady = !isNil(workflowTemplate) && !loading;

  // A draft that matches the saved order again is no edit at all. Left in place, it would
  // override the saved layout if that changed later.
  useEffect(() => {
    if (isReady && !isNil(draftOrder) && !isModified) {
      setDraftOrder(null);
    }
  }, [isReady, draftOrder, isModified]);

  // Read through a ref so a run of updates in one go (Show All) chains off each other rather
  // than each starting again from the last render, and so saving needs no new identity per edit.
  const latestRef = useRef({ visibleOrder, savedLayout, isModified });
  useEffect(() => {
    latestRef.current = { visibleOrder, savedLayout, isModified };
  }, [visibleOrder, savedLayout, isModified]);

  const updateComponentOrder = useCallback<UpdateComponentOrder>(update => {
    setDraftOrder(prev => update(prev ?? latestRef.current.visibleOrder));
  }, []);

  const revertLayout = useCallback(() => setDraftOrder(null), []);

  const saveLayout = useCallback(async () => {
    const latest = latestRef.current;
    if (!latest.isModified) {
      return;
    }
    try {
      await saveLayoutOrder({
        workflowTemplateId,
        viewTemplateId,
        componentIds: latest.visibleOrder,
        layout: latest.savedLayout,
      });
      setDraftOrder(null);
    } catch (error) {
      console.error(error);
      toast({ style: 'warning', message: i18n('errors.somethingWentWrong') });
    }
  }, [saveLayoutOrder, workflowTemplateId, viewTemplateId, toast]);

  usePreserveViewLayout({
    storageKey: getViewLayoutStorageKey(viewTemplateId),
    isEnabled: preserveEdits,
    isReady,
    draftOrder,
    isModified,
    setDraftOrder,
  });

  const isLoading = loading && !savedLayout;
  const state = useMemo(() => ({ layout, isLoading, isModified }), [layout, isLoading, isModified]);
  const actions = useMemo(
    () => ({ updateComponentOrder, revertLayout, saveLayout }),
    [updateComponentOrder, revertLayout, saveLayout],
  );

  return { state, actions };
}

/**
 * Keeps an unsaved order in local storage, as `PreserveViewTemplate` does for the rest of the
 * view's edits. It stays out of the URL: a list of every visible field would swamp the link.
 */
function usePreserveViewLayout({
  storageKey,
  isEnabled,
  isReady,
  draftOrder,
  isModified,
  setDraftOrder,
}: {
  storageKey: string;
  isEnabled: boolean;
  isReady: boolean;
  draftOrder: string[] | null;
  isModified: boolean;
  setDraftOrder: (order: string[]) => void;
}) {
  const hasReadStorageRef = useRef(false);

  useEffect(() => {
    if (!isEnabled) {
      return;
    }
    const storedOrder = JsonStorage.get<string[]>(storageKey);
    hasReadStorageRef.current = true;
    if (!isNil(storedOrder)) {
      setDraftOrder(storedOrder);
    }
    return () => {
      // Re-read on a remount before anything is allowed to clear the stored order.
      hasReadStorageRef.current = false;
    };
  }, [storageKey, isEnabled, setDraftOrder]);

  useEffect(() => {
    if (!isEnabled || !hasReadStorageRef.current) {
      return;
    }
    if (isModified && !isNil(draftOrder)) {
      JsonStorage.set(storageKey, draftOrder, VIEW_LAYOUT_TTL);
    } else if (isReady) {
      // Only once the orders can be compared: before that, a restored order isn't modified yet.
      JsonStorage.remove(storageKey);
    }
  }, [storageKey, isEnabled, isReady, draftOrder, isModified]);
}

/**
 * The ids of the fields a collection view shows, in the order it shows them: what its layout
 * holds once saved.
 */
export function getVisibleFieldOrder(
  components: readonly API.AnyComponent[],
  layout: API.ViewTemplateLayoutFragment | null | undefined,
  viewTemplate: API.ViewTemplateFragment,
): string[] {
  const viewComponents = components.filter(
    component =>
      !isManagedComponent(component) && !isComponentExcludedFromCollectionView(component),
  );
  return getComponentsInLayoutOrder(viewComponents, layout)
    .filter(component => {
      const viewOptions = viewTemplate.componentsViewOptions?.find(
        options => options.componentId === component.id,
      );
      return !isComponentHiddenInView(viewTemplate, viewOptions, component);
    })
    .map(component => component.id);
}

function toDraftLayout(
  componentIds: string[],
  savedLayout: API.ViewTemplateLayoutFragment | null | undefined,
): API.ViewTemplateLayoutFragment {
  return {
    id: savedLayout?.id ?? 'draft',
    items: componentIds.map(componentId => ({
      __typename: 'ViewTemplateLayoutComponent',
      id: `draft-${componentId}`,
      type: 'SINGLE_COMPONENT',
      componentId,
    })),
  } as API.ViewTemplateLayoutFragment;
}
